import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import uploadWithProgress from "../../../lib/uploadWithProgress";
import { photoAngles } from "../constants";
import {
  confirmVehiclePhoto,
  vehiclePhotoUploadUrl,
  vehiclePhotosForBooking,
} from "./queries";
import { friendlyValetError } from "./valetErrors";

const DEFAULT_CONTENT_TYPE = "image/jpeg";

const idleState = () =>
  photoAngles.reduce(
    (acc, { angle }) => ({ ...acc, [angle]: { status: "idle", progress: 0 } }),
    {},
  );

/*
 * The vehicle condition photos for one stage of one valet session: capture ->
 * presigned PUT -> confirm, one independent lifecycle per angle. The stage is
 * required - INTAKE and HANDOVER are separate sets and must never be mixed.
 */
export default function useVehiclePhotos({
  bookingId,
  facilityId,
  stage,
  valetSessionId = null,
}) {
  const [photos, setPhotos] = useState(idleState);

  /* Kept in refs, not state: none of these should trigger a render. */
  const blobsRef = useRef({}); // last blob per angle, for Retry
  const abortsRef = useRef({}); // AbortController per in-flight angle
  const previewsRef = useRef({}); // object URL per angle, awaiting revocation
  const bookingIdRef = useRef(bookingId);

  const [runUploadUrl] = useMutation(vehiclePhotoUploadUrl);
  const [runConfirm] = useMutation(confirmVehiclePhoto);

  /*
   * viewUrl is a short-lived presigned GET, so a cached one would 404/403 by the
   * time it renders. Always go to the network.
   */
  const { data: hydrateData, loading: hydrating } = useQuery(
    vehiclePhotosForBooking,
    {
      variables: { bookingId, facilityId, stage, valetSessionId },
      skip: !bookingId || !facilityId || !stage,
      fetchPolicy: "network-only",
    },
  );

  const setPreviewUrl = useCallback((angle, url) => {
    const previous = previewsRef.current[angle];
    if (previous && previous !== url) URL.revokeObjectURL(previous);
    if (url) previewsRef.current[angle] = url;
    else delete previewsRef.current[angle];
  }, []);

  const revokeAll = useCallback(() => {
    Object.values(previewsRef.current).forEach((url) =>
      URL.revokeObjectURL(url),
    );
    previewsRef.current = {};
  }, []);

  const abortAll = useCallback(() => {
    Object.values(abortsRef.current).forEach((controller) =>
      controller?.abort(),
    );
    abortsRef.current = {};
  }, []);

  /* Feeds the staleness guard below - an upload started for one car must not
   * write onto another after a release. */
  useEffect(() => {
    bookingIdRef.current = bookingId;
  }, [bookingId]);

  const reset = useCallback(() => {
    abortAll();
    revokeAll();
    blobsRef.current = {};
    setPhotos(idleState());
  }, [abortAll, revokeAll]);

  /* Photos already on the booking for this stage: show them rather than making
   * the runner shoot all four again after a tablet reload. */
  useEffect(() => {
    const existing = hydrateData?.vehiclePhotos;
    if (!existing?.length) return;

    setPhotos((prev) => {
      const next = { ...prev };
      existing.forEach((photo) => {
        /* never clobber something captured on this tablet since the query ran */
        if (next[photo.angle] && next[photo.angle].status !== "idle") return;
        next[photo.angle] = {
          status: "done",
          progress: 1,
          photoId: photo.id,
          imageUrl: photo.viewUrl,
        };
      });
      return next;
    });
  }, [hydrateData]);

  useEffect(
    () => () => {
      abortAll();
      revokeAll();
    },
    [abortAll, revokeAll],
  );

  const upload = useCallback(
    async (angle, { blob, width, height }) => {
      if (!bookingId || !facilityId) {
        setPhotos((p) => ({
          ...p,
          [angle]: {
            ...p[angle],
            status: "error",
            error: "This car has no booking yet, so the photo cannot be saved.",
          },
        }));
        return;
      }

      /* One presign per attempt, so the previous attempt's URL is abandoned. */
      abortsRef.current[angle]?.abort();
      const controller = new AbortController();
      abortsRef.current[angle] = controller;

      /* One variable for both the presign and the PUT header: a byte of
       * divergence and S3 rejects the signature. */
      const contentType = blob.type || DEFAULT_CONTENT_TYPE;
      const previewUrl = URL.createObjectURL(blob);
      setPreviewUrl(angle, previewUrl);

      const startedFor = bookingId;
      /* Bail out if the car changed under us - a late setPhotos would otherwise
       * paint this photo onto the next car's tile. */
      const stale = () =>
        bookingIdRef.current !== startedFor || controller.signal.aborted;

      setPhotos((p) => ({
        ...p,
        [angle]: {
          status: "uploading",
          progress: 0,
          imageUrl: previewUrl,
          photoId: p[angle]?.photoId,
        },
      }));

      try {
        const { data } = await runUploadUrl({
          variables: { bookingId, facilityId, angle, stage, contentType },
        });
        const target = data?.vehiclePhotoUploadUrl;
        if (!target) throw new Error("No upload target was returned.");
        if (stale()) return;

        await uploadWithProgress(target.uploadUrl, blob, {
          contentType: target.contentType,
          signal: controller.signal,
          onProgress: (progress) => {
            if (stale()) return;
            setPhotos((p) =>
              p[angle]?.status === "uploading"
                ? { ...p, [angle]: { ...p[angle], progress } }
                : p,
            );
          },
        });
        if (stale()) return;

        const { data: confirmed } = await runConfirm({
          variables: {
            input: {
              bookingId,
              facilityId,
              angle,
              stage,
              valetSessionId,
              key: target.key,
              contentType: target.contentType,
              sizeBytes: blob.size,
              width: width ?? null,
              height: height ?? null,
            },
          },
        });
        if (stale()) return;

        const photo = confirmed?.confirmVehiclePhoto;
        setPhotos((p) => ({
          ...p,
          [angle]: {
            status: "done",
            progress: 1,
            /* keep the local preview: it needs no round-trip and no signature */
            imageUrl: previewUrl,
            photoId: photo?.id,
          },
        }));
      } catch (e) {
        /* A deliberate abort is not a failure the concierge needs to see. */
        if (e?.message === "UPLOAD_ABORTED" || stale()) return;

        setPhotos((p) => ({
          ...p,
          [angle]: {
            ...p[angle],
            status: "error",
            imageUrl: previewUrl,
            error: friendlyValetError(e, "upload the photo"),
          },
        }));
      } finally {
        if (abortsRef.current[angle] === controller) {
          delete abortsRef.current[angle];
        }
      }
    },
    [
      bookingId,
      facilityId,
      stage,
      valetSessionId,
      runUploadUrl,
      runConfirm,
      setPreviewUrl,
    ],
  );

  const capture = useCallback(
    (angle, blob, { width, height } = {}) => {
      blobsRef.current[angle] = { blob, width, height };
      return upload(angle, { blob, width, height });
    },
    [upload],
  );

  const retry = useCallback(
    (angle) => {
      const pending = blobsRef.current[angle];
      if (!pending) return undefined;
      return upload(angle, pending);
    },
    [upload],
  );

  const summary = useMemo(() => {
    const states = photoAngles.map(({ angle }) => photos[angle] ?? {});
    return {
      doneCount: states.filter((s) => s.status === "done").length,
      allDone: states.every((s) => s.status === "done"),
      anyBusy: states.some((s) => s.status === "uploading"),
      anyError: states.some((s) => s.status === "error"),
    };
  }, [photos]);

  return { photos, capture, retry, reset, hydrating, ...summary };
}
